import {
    CateringAppActions,
    CateringCheckoutActions,
    CateringHandoffActions,
    CateringMenuActions,
    CateringUserActions,
    handoffActions,
    menuActions,
    RootState,
    userActions,
    checkoutActions,
    appActions,
} from '@maverick/store';
import { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Helmet } from 'react-helmet';
import jwt_decode from 'jwt-decode';
import {
    SourceApps,
    OrderConfig,
    RecentOrder,
    TokenProvider,
    TransactionsHistory,
    UserRewards,
    UserRecentOrders,
    RewardWallet,
    Rewards,
    Cookies,
} from '@maverick/entity';
import { GaEvent, GetCookie, checkRestaurantAvailability, handleRestaurantInfo } from '@maverick/utils';
import { fetchUserInfoFromToken, filterRewards, toastError, isBasketExpired } from './Auth.utils';
import type { OrderUi as OrderUiType } from '@maverick/themes';
import type { ToastProps as ToastPropsType } from '@maverick/ui';

import { UserManager } from '../managers/User.manager';
import { CheckoutManager } from '../managers/Checkout.manager';
import { HandoffManager } from '../managers/Handoff.manager';

const logoutOrderRedirectURI = process.env.AUTH0_LOGOUT_REDIRECT_URI;
const logoutCateringRedirectURI = process.env.AUTH0_LOGOUT_REDIRECT_URI_CATERING;

interface AuthenticationProps {
    Config: Partial<OrderConfig>;
    Log: any;
    orderUi: OrderUiType;
    navigate: (target: string) => void;
    sourceApp: SourceApps;
    setToast: (toast: ToastPropsType) => void;
}

export const Authentication: FC<AuthenticationProps> = ({ Config, Log, orderUi, navigate, sourceApp, setToast }) => {
    const pageTitle = 'Authentication';
    const orderCheckoutManager = new CheckoutManager(SourceApps.order);
    const orderHandoffManager = new HandoffManager(SourceApps.order);
    const orderUserManager = new UserManager(SourceApps.order);

    const cateringCheckoutManager = new CheckoutManager(SourceApps.catering);
    const cateringHandoffManager = new HandoffManager(SourceApps.catering);
    const cateringUserManager = new UserManager(SourceApps.catering);

    const { redirectRoute: orderRedirectRoute } = useSelector((state: RootState) => state.app);
    const { redirectRoute: cateringRedirectRoute } = useSelector((state: RootState) => state.catering.app);
    const orderUserAuthLastInteraction = useSelector((state: RootState) => state.user.userAuthLastInteraction);
    const cateringUserAuthLastInteraction = useSelector(
        (state: RootState) => state.catering.user.userAuthLastInteraction
    );

    const orderBasket = useSelector((state: RootState) => state.checkout.basket);
    const orderBasketCreationDate = useSelector((state: RootState) => state.checkout.basketCreationDate);
    const selectedRestaurant = useSelector((state: RootState) => state.handoff.selectedRestaurant);

    const cateringBasket = useSelector((state: RootState) => state.catering.checkout.basket);
    const cateringBasketCreationDate = useSelector((state: RootState) => state.catering.checkout.basketCreationDate);
    const cateringSelectedRestaurant = useSelector((state: RootState) => state.catering.handoff.selectedRestaurant);

    const { isAuthenticated, isLoading, getAccessTokenSilently, error, logout, loginWithRedirect, user } = useAuth0();
    const dispatch = useDispatch();

    const [orderUserTokenProvider, setOrderUserTokenProvider] = useState<TokenProvider>();
    const [cateringUserTokenProvider, setCateringUserTokenProvider] = useState<TokenProvider>();

    const [cateringLastOrder, setCateringLastOrder] = useState<RecentOrder>();
    const [orderLastOrder, setOrderLastOrder] = useState<RecentOrder>();

    const [finishedOrderProcessing, setFinishedOrderProcessing] = useState<boolean>(false);
    const [finishedCateringProcessing, setFinishedCateringProcessing] = useState<boolean>(false);
    const [redirectRestaurantOrder, setRedirectRestaurantOrder] = useState<string>('');
    const [redirectRestaurantCatering, setRedirectRestaurantCatering] = useState<string>('');

    useEffect(() => {
        handleLogin();
    }, [isAuthenticated, isLoading, error]);
    useEffect(() => {
        if (orderUserTokenProvider && cateringUserTokenProvider) {
            retrieveUserData();
            handleOrderExistingBasket();
            handleCateringExistingBasket();
        }
    }, [orderUserTokenProvider, cateringUserTokenProvider]);

    useEffect(() => {
        if (!orderBasket && orderLastOrder) {
            handleOrderUserWithoutBasket();
        }
    }, [orderLastOrder]);

    useEffect(() => {
        if (!cateringBasket && cateringLastOrder) {
            handleCateringUserWithoutBasket();
        }
    }, [cateringLastOrder]);

    useEffect(() => {
        if (finishedOrderProcessing && finishedCateringProcessing) {
            if (sourceApp === SourceApps.catering) {
                if (redirectRestaurantCatering) {
                    setToast({
                        variant: 'success',
                        text: `You've been redirected to your last order restaurant!`,
                    });
                }

                redirectRestaurantCatering ? redirectToPage(redirectRestaurantCatering) : redirectToPage();
            } else {
                if (redirectRestaurantOrder) {
                    setToast({
                        variant: 'success',
                        text: `You've been redirected to your last order restaurant!`,
                    });
                }

                redirectRestaurantOrder ? redirectToPage(redirectRestaurantOrder) : redirectToPage();
            }
        }
    }, [finishedOrderProcessing, finishedCateringProcessing]);

    const reduxClear = async () => {
        dispatch(CateringAppActions.resetApp());
        dispatch(appActions.resetApp());

        dispatch(CateringCheckoutActions.resetCheckout());
        dispatch(checkoutActions.resetCheckout());

        dispatch(CateringHandoffActions.resetHandoff());
        dispatch(handoffActions.resetHandoff());

        dispatch(CateringUserActions.resetUser());
        dispatch(userActions.resetUser());

        dispatch(CateringMenuActions.resetMenu());
        dispatch(menuActions.resetMenu());
    };

    const handleRedeemableRewards = async (rewardsWallets: RewardWallet[], responseRewards: Rewards[]) => {
        const redeemable = rewardsWallets.filter((reward) => {
            const isRedeemable = responseRewards.some(
                (r: any) => r.externalreference.replace(/\D/g, '') === reward.id.toString()
            );
            return isRedeemable;
        });
        return redeemable;
    };

    const redirectToPage = (route?: string) => {
        if (sourceApp === SourceApps.catering) {
            const redirectTo = route ?? cateringRedirectRoute ?? '/';

            dispatch(CateringAppActions.clearRedirectRoute);
            navigate(redirectTo);
            dispatch(CateringAppActions.setLoading(false));
        } else {
            const redirectTo = route ?? orderRedirectRoute ?? '/';

            dispatch(appActions.clearRedirectRoute());
            navigate(redirectTo);
            dispatch(appActions.setLoading(false));
        }
    };

    const setErrorToast = () => {
        setToast({
            variant: 'error',
            text: toastError,
        });

        GaEvent.ErrorMessage(toastError, pageTitle);
    };

    const handleOrderUserWithoutBasket = async () => {
        if (!orderUserTokenProvider || !orderLastOrder) {
            setFinishedOrderProcessing(true);
            return;
        }

        const { vendorid, deliverymode } = orderLastOrder;
        const [restaurant, calendar] = await Promise.all([
            orderHandoffManager.getRestaurant(vendorid),
            orderHandoffManager.getRestaurantCalendarById(vendorid),
        ]);

        if (!restaurant || !calendar) {
            setFinishedOrderProcessing(true);
            return;
        }

        const lastRestaurant = { ...restaurant, calendar: calendar.calendar ?? [] };
        const { restaurantAddress } = handleRestaurantInfo(lastRestaurant);
        const isLastRestaurantAvailable = checkRestaurantAvailability(lastRestaurant, deliverymode);

        if (!isLastRestaurantAvailable) {
            setFinishedOrderProcessing(true);
            return;
        }

        dispatch(handoffActions.setSelectedRestaurant(lastRestaurant));
        dispatch(handoffActions.setSelectedRestaurantAddress(restaurantAddress));
        dispatch(handoffActions.setSelectedMethod(deliverymode));

        setRedirectRestaurantOrder(`/menu/${lastRestaurant.slug}`);
        setFinishedOrderProcessing(true);
    };

    const handleCateringUserWithoutBasket = async () => {
        if (!cateringUserTokenProvider || !cateringLastOrder) {
            setFinishedCateringProcessing(true);
            return;
        }

        const { vendorid, deliverymode } = cateringLastOrder;
        const [restaurant, calendar] = await Promise.all([
            cateringHandoffManager.getRestaurant(vendorid),
            cateringHandoffManager.getRestaurantCalendarById(vendorid),
        ]);
        if (!restaurant || !calendar) {
            setFinishedCateringProcessing(true);
            return;
        }

        const lastRestaurant = { ...restaurant, calendar: calendar.calendar ?? [] };
        const { restaurantAddress } = handleRestaurantInfo(lastRestaurant);
        const isLastRestaurantAvailable = checkRestaurantAvailability(lastRestaurant, deliverymode);
        if (!isLastRestaurantAvailable) {
            setFinishedCateringProcessing(true);
            return;
        }

        dispatch(CateringHandoffActions.setSelectedRestaurant(lastRestaurant));
        dispatch(CateringHandoffActions.setSelectedRestaurantAddress(restaurantAddress));
        dispatch(CateringHandoffActions.setSelectedMethod(deliverymode));

        setRedirectRestaurantCatering(`/menu/${lastRestaurant.slug}`);
        setFinishedCateringProcessing(true);
    };

    const handleOrderExistingBasket = async () => {
        if (!orderBasket || !orderUserTokenProvider) return;

        const staleBasket = isBasketExpired(orderBasketCreationDate);
        if (!staleBasket) {
            const { retrieveBasket } = await orderCheckoutManager.getBasket(orderBasket.id);

            if (retrieveBasket) {
                dispatch(checkoutActions.setBasket(retrieveBasket));
            }
            setFinishedOrderProcessing(true);
            return;
        }
        dispatch(checkoutActions.setBasket(null));
        setFinishedOrderProcessing(true);
    };

    const handleCateringExistingBasket = async () => {
        if (!cateringBasket || !cateringUserTokenProvider) return;

        const staleBasket = isBasketExpired(cateringBasketCreationDate);
        if (!staleBasket) {
            const { retrieveBasket } = await cateringCheckoutManager.getBasket(cateringBasket.id);

            if (retrieveBasket) {
                dispatch(CateringCheckoutActions.setBasket(retrieveBasket));
            }
            setFinishedCateringProcessing(true);
            return;
        }
        dispatch(CateringCheckoutActions.setBasket(null));
        setFinishedCateringProcessing(true);
    };

    const handleOrderUserRewards = async (history: TransactionsHistory, emailAddress: string) => {
        const userRewards = UserRewards.Make(history);
        dispatch(userActions.setUserRewards(userRewards));

        if (orderBasket) {
            if (!orderUserTokenProvider) return;

            const { authtoken } = orderUserTokenProvider;

            const { responseRewards, error } = await orderUserManager.getQualifyingRewards(
                authtoken,
                orderBasket.vendorid
            );

            if (responseRewards && !error) {
                const redeemable = await handleRedeemableRewards(userRewards.rewardsWallets, responseRewards);
                dispatch(userActions.setUserRedeemableRewards(redeemable));
            }
        }

        const filtered15DaysRewardsCount = filterRewards(userRewards.rewardsWallets, 15).length;
        const filtered30DaysRewardsCount = filterRewards(userRewards.rewardsWallets, 30).length;
        GaEvent.UserExpiringRewards(emailAddress, filtered15DaysRewardsCount, filtered30DaysRewardsCount);

        GaEvent.Global.Set(
            'event_label',
            `${filtered15DaysRewardsCount}|${filtered30DaysRewardsCount}|${
                userRewards.rewardsWallets.length - filtered30DaysRewardsCount
            }`
        );
    };

    const handleCateringUserRewards = async (history: TransactionsHistory, emailAddress: string) => {
        const userRewards = UserRewards.Make(history);

        dispatch(CateringUserActions.setUserRewards(userRewards));

        if (cateringBasket) {
            if (!cateringUserTokenProvider) return;

            const { authtoken } = cateringUserTokenProvider;

            const { responseRewards, error } = await cateringUserManager.getQualifyingRewards(
                authtoken,
                cateringBasket.vendorid
            );

            if (responseRewards && !error) {
                const redeemable = await handleRedeemableRewards(userRewards.rewardsWallets, responseRewards);
                dispatch(CateringUserActions.setUserRedeemableRewards(redeemable));
            }
        }

        const filtered15DaysRewardsCount = filterRewards(userRewards.rewardsWallets, 15).length;
        const filtered30DaysRewardsCount = filterRewards(userRewards.rewardsWallets, 30).length;
        GaEvent.UserExpiringRewards(emailAddress, filtered15DaysRewardsCount, filtered30DaysRewardsCount);

        GaEvent.Global.Set(
            'event_label',
            `${filtered15DaysRewardsCount}|${filtered30DaysRewardsCount}|${
                userRewards.rewardsWallets.length - filtered30DaysRewardsCount
            }`
        );
    };

    const retrieveUserData = async () => {
        if (!orderUserTokenProvider || !cateringUserTokenProvider) return;

        const {
            authtoken: orderAuthToken,
            firstname: orderFirstName,
            lastname: orderLastName,
            contactnumber: orderContactNumber,
            emailaddress: orderEmailAddress,
        } = orderUserTokenProvider;

        const {
            authtoken: cateringAuthToken,
            firstname: cateringFirstName,
            lastname: cateringLastName,
            contactnumber: cateringContactNumber,
            emailaddress: cateringEmailAddress,
        } = cateringUserTokenProvider;

        dispatch(CateringAppActions.setUserToken(cateringAuthToken));
        dispatch(appActions.setUserToken(orderAuthToken));

        dispatch(
            CateringUserActions.setUserInfo({
                firstName: cateringFirstName,
                lastName: cateringLastName,
                phone: cateringContactNumber,
                email: cateringEmailAddress,
            })
        );

        dispatch(
            userActions.setUserInfo({
                firstName: orderFirstName,
                lastName: orderLastName,
                phone: orderContactNumber,
                email: orderEmailAddress,
            })
        );

        const [orderFaves, orderRecents, orderHistory, cateringFaves, cateringRecents, cateringHistory] =
            await Promise.all([
                orderUserManager.retrieveUserFaves(orderAuthToken),
                orderUserManager.retrieveUserRecentOrders(orderAuthToken),
                orderUserManager.getTransactionsHistoryByEmail(orderEmailAddress),

                cateringUserManager.retrieveUserFaves(cateringAuthToken),
                cateringUserManager.retrieveUserRecentOrders(cateringAuthToken),
                cateringUserManager.getTransactionsHistoryByEmail(cateringEmailAddress),
            ]);

        if (orderFaves.response) {
            dispatch(userActions.setUserFaves(orderFaves.response));
        }
        if (cateringFaves.response) {
            dispatch(CateringUserActions.setUserFaves(cateringFaves.response));
        }

        if (orderHistory.transactionsHistory) {
            handleOrderUserRewards(orderHistory.transactionsHistory, orderEmailAddress);
        }

        if (cateringHistory.transactionsHistory) {
            handleCateringUserRewards(cateringHistory.transactionsHistory, cateringEmailAddress);
        }

        if (orderRecents.response) handleRecentOrdersAndRedirect(orderRecents.response, 'order');

        if (cateringRecents.response) handleRecentOrdersAndRedirect(cateringRecents.response, 'catering');
    };

    const handleRecentOrdersAndRedirect = (recentOrders: UserRecentOrders, app: 'order' | 'catering') => {
        const isOrder = app === 'order';

        dispatch(
            isOrder
                ? userActions.setUserRecentOrders(recentOrders)
                : CateringUserActions.setUserRecentOrders(recentOrders)
        );

        const basket = isOrder ? orderBasket : cateringBasket;
        if (basket) return;

        const hasFavoriteStore = GetCookie(Cookies.FavoriteStore);

        if (!hasFavoriteStore && recentOrders.orders.length >= 1) {
            isOrder ? setOrderLastOrder(recentOrders.orders[0]) : setCateringLastOrder(recentOrders.orders[0]);
            return;
        }

        const restaurant = isOrder ? selectedRestaurant : cateringSelectedRestaurant;
        if (hasFavoriteStore && restaurant) {
            dispatch(
                isOrder
                    ? appActions.setRedirectRoute(`/menu/${restaurant.slug}`)
                    : CateringAppActions.setRedirectRoute(`/menu/${restaurant.slug}`)
            );
        }

        isOrder ? setFinishedOrderProcessing(true) : setFinishedCateringProcessing(true);
    };

    const authorizeUserOnOlo = async () => {
        const token = await getAccessTokenSilently();
        const decodedToken = jwt_decode(token);

        if (!token) {
            return null;
        }

        const { givenName, familyName, email, mobileNumber, newUser } = fetchUserInfoFromToken(decodedToken);

        const { orderTokenProvider, orderError, cateringTokenProvider, cateringError } =
            await cateringUserManager.authorizeUser(
                orderUi.app.providerAuth0,
                token,
                'providertoken',
                mobileNumber,
                orderBasket?.id ?? null,
                cateringBasket?.id ?? null,
                givenName,
                familyName,
                email,
                newUser
            );

        if (!orderTokenProvider || orderError || !cateringTokenProvider || cateringError) {
            const logObject = {
                userInfo: {
                    isAuthenticated: false,
                    firstName: givenName,
                    lastName: familyName,
                    phone: mobileNumber,
                    email: email,
                },
            };
            Log.Error('Login', logObject);
            return null;
        }

        if (orderTokenProvider.newUser && orderUserAuthLastInteraction)
            GaEvent.RegistrationUserSuccess(orderUserAuthLastInteraction);
        if (cateringTokenProvider.newUser && cateringUserAuthLastInteraction)
            GaEvent.RegistrationUserSuccess(cateringUserAuthLastInteraction);
        GaEvent.UserHasLoggedIn();
        return { orderTokenProvider, cateringTokenProvider };
    };

    const handleLogin = async () => {
        if (isLoading) {
            dispatch(CateringAppActions.setLoading(true));
            dispatch(appActions.setLoading(true));
            return;
        }

        if (error) {
            loginWithRedirect({ source: Config.Source });
            return;
        }

        if (isAuthenticated) {
            const tokenProvider = await authorizeUserOnOlo();

            if (!tokenProvider?.orderTokenProvider || !tokenProvider?.cateringTokenProvider) {
                const auth0RedirectURI = String(
                    sourceApp === SourceApps.catering ? logoutCateringRedirectURI : logoutOrderRedirectURI
                );

                reduxClear();

                logout({ returnTo: auth0RedirectURI });

                dispatch(CateringAppActions.setLoading(false));
                dispatch(appActions.setLoading(false));

                setErrorToast();
                return;
            }

            setOrderUserTokenProvider(tokenProvider.orderTokenProvider);
            setCateringUserTokenProvider(tokenProvider.cateringTokenProvider);
            return;
        }

        reduxClear();

        dispatch(CateringAppActions.setLoading(false));
        dispatch(appActions.setLoading(false));
        navigate('/');
    };

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
        </>
    );
};
